import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { ChatHistoryHeader } from "../../../components/sidekick/historyView/ChatHistoryHeader";
import { ChatHistoryTable } from "../../../components/sidekick/historyView/ChatHistoryTable";
import { SK_CHAT_HISTORY_CONTAINER_CLASS, UNAUTHORIZED_REDIRECT_DESCRIPTION, UNAUTHORIZED_STATUS_CODE, GENERIC_ACTION_ERROR_TITLE, GENERIC_ACTION_ERROR_DESCRIPTION } from "../../../constants/sidekick";
import "./ChatHistoryContainer.scss";
import { useChatHistory } from "../../../hooks/useChatHistory";
import { useDispatch, useSelector } from "react-redux";
import { clearChatDetail, selectChatHistory } from "../../../features/sidekick/sidekickSlice";
import { Snackbar, SnackbarMessage, Typography } from "@panviva/panviva-react-ui";
import { forceLogoutToLogin } from "../../../utils/auth/forceLogout";

const ChatHistoryContainer: FunctionComponent = () => {
    const dispatch = useDispatch();
    const chatHistoryState = useSelector(selectChatHistory);
    const [showActionErrorToast, setShowActionErrorToast] = useState(false);
    const [actionToastTrigger, setActionToastTrigger] = useState(0);

    const openActionErrorToast = () => {
        setShowActionErrorToast(true);
        setActionToastTrigger((prev) => prev + 1);
    };

    // Use the Redux chat history hook
    const chatHistoryProps = useChatHistory();

    // Clear chat detail state when the history view mounts
    // This ensures that when navigating back from a detail view,
    // the detail state is cleared and will be fetched fresh next time
    useEffect(() => {
        dispatch(clearChatDetail());
    }, [dispatch]);

    // Derived entirely from Redux state (error, data, lastRequestSource) — no local state involved.
    // This ensures controls automatically re-enable when a subsequent fetch succeeds and Redux
    // clears the error, preventing stale disabled UI after error recovery.
    const isInitialLoadFailure = useMemo(() => {
        return Boolean(
            chatHistoryState.error &&
            chatHistoryState.data.length === 0 &&
            chatHistoryState.lastRequestSource === 'initial'
        );
    }, [chatHistoryState.error, chatHistoryState.data.length, chatHistoryState.lastRequestSource]);

    // Disable sorting and filtering when there are no chat sessions and no active filters
    const hasNoChatSessions = useMemo(() => {
        return Boolean(
            !chatHistoryState.loading &&
            !chatHistoryState.error &&
            chatHistoryState.data.length === 0 &&
            !chatHistoryProps.hasActiveFilters
        );
    }, [chatHistoryState.loading, chatHistoryState.error, chatHistoryState.data.length, chatHistoryProps.hasActiveFilters]);

    const isUnauthorizedInitialLoadFailure =
        isInitialLoadFailure && chatHistoryState.errorStatusCode === UNAUTHORIZED_STATUS_CODE;

    const isUnauthorizedActionFailure = Boolean(
        chatHistoryState.errorStatusCode === UNAUTHORIZED_STATUS_CODE &&
        chatHistoryState.error &&
        (chatHistoryState.lastRequestSource === 'filter' ||
            chatHistoryState.lastRequestSource === 'pagination' ||
            chatHistoryState.lastRequestSource === 'sorting')
    );

    useEffect(() => {
        const isActionFailure = Boolean(
            chatHistoryState.error &&
            (chatHistoryState.lastRequestSource === 'filter' ||
                chatHistoryState.lastRequestSource === 'pagination' ||
                chatHistoryState.lastRequestSource === 'sorting')
        );

        if (isActionFailure) {
            openActionErrorToast();
        }
    }, [chatHistoryState.error, chatHistoryState.lastRequestSource]);

    useEffect(() => {
        if (!showActionErrorToast) {
            return;
        }

        // Note: each new error (including rapid successive failures) resets this timer via
        // actionToastTrigger, so the toast stays visible for 4 seconds from the most recent
        // error. This is intentional — the last error wins and there is no visual flicker
        // because showActionErrorToast remains true throughout.
        const timeoutId = window.setTimeout(() => {
            setShowActionErrorToast(false);
        }, 4000);

        return () => window.clearTimeout(timeoutId);
    }, [showActionErrorToast, actionToastTrigger]);

    useEffect(() => {
        if (!isUnauthorizedInitialLoadFailure && !isUnauthorizedActionFailure) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            forceLogoutToLogin();
        }, 4000);

        return () => window.clearTimeout(timeoutId);
    }, [isUnauthorizedInitialLoadFailure, isUnauthorizedActionFailure, actionToastTrigger]);

    return (
        <div className={SK_CHAT_HISTORY_CONTAINER_CLASS} role="main" aria-labelledby="chat-history-title">
            <ChatHistoryHeader
                disableFilterButton={isInitialLoadFailure || hasNoChatSessions}
                onFilterLookupFailure={openActionErrorToast}
            />

            <ChatHistoryTable
                {...chatHistoryProps}
                isInitialLoadFailure={isInitialLoadFailure}
                disableSorting={isInitialLoadFailure || hasNoChatSessions}
                isUnauthorizedInitialLoadFailure={isUnauthorizedInitialLoadFailure}
            />

            {/* Show Snackbar for any filter/pagination/sorting action error and unauthorized action failure */}
            {(showActionErrorToast || isUnauthorizedActionFailure) && (
                // Wrapper div with ARIA attributes to ensure screen reader announcements
                // This is a workaround since @panviva/panviva-react-ui Snackbar doesn't support ARIA props directly
                <div role="alert" aria-live="assertive" aria-atomic="true">
                    <Snackbar className={'chat-history-error-container'} status="error" icon='alert'>
                        <SnackbarMessage className='error-content'>
                            <Typography variant="body_2" className='error-text'>
                                {isUnauthorizedActionFailure
                                    ? UNAUTHORIZED_REDIRECT_DESCRIPTION
                                    : (GENERIC_ACTION_ERROR_TITLE + " " + GENERIC_ACTION_ERROR_DESCRIPTION)}
                            </Typography>
                        </SnackbarMessage>
                    </Snackbar>
                </div>
            )}
        </div>
    );
}

export default ChatHistoryContainer;